import React, { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setAuthToken } from './http-common';
import { Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import "./App.css";
import PageLoader from "./components/page-loader";
import { AuthenticationGuard } from "./components/authentication-guard";
import LoginButton from "./components/LoginButton";
import ProfileButton from "./components/ProfilButton";
import MonProfil from "./components/mon-profil.component";
import UserProfil from "./components/user-profil.component";
import HomeAdmin from "./components/home-admin.component"; 
import HomeMng from "./components/home-mng.component";
import HomeSec from "./components/home-sec.component";
import HomeMed from "./components/home-med.component"; 
import HomePat from "./components/home-pat.component"; 
import ShowStructure from './components/show-structure.component';
import RecetteGlobale from "./components/recette-globale.component";
import RecetteStructure from "./components/recette-structure.component";
import GlobalSearch from "./components/global-search.component";
import ResultsFromDoctor from "./components/results-from-doctor.component";
import ResultsFromStructure from "./components/results-from-structure.component";
import ResultsFromSpecialiteLieu from "./components/results-from-specialite-lieu.component";
import ConfirmRdv from "./components/confirm-rdv.component";
import EmailVerificationPage from './components/EmailVerificationPage';
import { EmailVerifiedGuard } from "./components/email-verified-guard";
import logger from "./utils/logger";

export const App = () => {
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const accessTokenFetchedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (location.pathname === '/verify-email') {
      return;
    }

    if (user && user.email_verified === false) {
      const returnTo = `${location.pathname}${location.search}`;
      navigate('/verify-email', { replace: true, state: { returnTo } });
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search, navigate, user]);

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (isAuthenticated) {
        if (location.pathname === '/verify-email') {
          return;
        }

        if (accessTokenFetchedRef.current) {
          return;
        }
        try {
          logger.log('Récupération du token Auth0...');
          const token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          });
          
          // Vérifier si c'est un JWT valide (3 parties séparées par des points)
          if (token && token.split('.').length === 3) {
            logger.log('Token Auth0 configuré avec succès');
            setAuthToken(token);
            accessTokenFetchedRef.current = true;
          } else {
            logger.error('Format de token invalide - pas un JWT');
          }
        } catch (error) {
          logger.error('Erreur lors de la récupération du token:', error.message);
        }
      } else {
        logger.log('Utilisateur non authentifié');
        // Vider le token si l'utilisateur n'est pas authentifié
        setAuthToken(null);
        accessTokenFetchedRef.current = false;
      }
    };
    fetchAccessToken();
  }, [getAccessTokenSilently, isAuthenticated, location.pathname]);

  // Canal inter-onglets pour synchroniser l'auth
  const authChannelRef = useRef(null);
  useEffect(() => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window && !authChannelRef.current) {
      authChannelRef.current = new BroadcastChannel('yemii_auth');
    }
    return () => {
      if (authChannelRef.current) {
        try { authChannelRef.current.close(); } catch (_) {}
        authChannelRef.current = null;
      }
    };
  }, []);

  // Fonction pour forcer une déconnexion complète
  const forceLogout = () => {
    try {
      localStorage.setItem('yemii_logout', String(Date.now()));
      localStorage.setItem('yemii_explicit_logout', '1');
      console.debug('[auth] forceLogout: marker set');
    } catch (e) { console.warn('[auth] forceLogout: localStorage failed', e); }
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('@@auth0spajs@@')) {
          localStorage.removeItem(k);
        }
      }
    } catch (e) { console.warn('[auth] forceLogout: auth0 cache clear failed', e); }
    // Invalider le token axios côté app
    try { authChannelRef.current && authChannelRef.current.postMessage({ type: 'logout' }); console.debug('[auth] forceLogout: broadcast sent'); } catch (e) { console.warn('[auth] forceLogout: broadcast failed', e); }
    setAuthToken(null);
    // Déconnexion Auth0 (fermeture de session côté fournisseur)
    logout({
      logoutParams: { returnTo: window.location.origin, federated: true }
    });
  };

  // Exposer la fonction globalement pour les composants
  window.forceLogout = forceLogout;

  useEffect(() => {
    // Handler commun pour déclencher la déconnexion locale
    const handleLocalSignOut = (source) => {
      try { console.debug('[auth] signout via', source); } catch(_) {}
      try { sessionStorage.clear(); } catch (_) {}
      try { setAuthToken(null); } catch (_) {}
      try { localStorage.removeItem('yemii_logout'); } catch (_) {}
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('@@auth0spajs@@')) {
            localStorage.removeItem(k);
          }
        }
      } catch (_) {}
      try { logout({ logoutParams: { returnTo: window.location.origin, federated: true } }); } catch (_) {}
    };

    // BroadcastChannel
    const onChannelMessage = (ev) => {
      if (ev?.data?.type === 'logout') {
        handleLocalSignOut('broadcast');
      }
    };
    if (authChannelRef.current) {
      authChannelRef.current.addEventListener('message', onChannelMessage);
    }

    // Fallback: storage event
    const onStorage = (e) => {
      if (e.key === 'yemii_logout') {
        handleLocalSignOut('storage');
      }
    };
    window.addEventListener('storage', onStorage);

    // Fallback additionnel: au retour en visibilité, vérifier un logout distant
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const marker = localStorage.getItem('yemii_logout');
        if (marker) {
          handleLocalSignOut('visibility');
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (authChannelRef.current) {
        authChannelRef.current.removeEventListener('message', onChannelMessage);
      }
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [logout, navigate]);

  if (isLoading) {
    return (
      <div className="page-layout">
        <PageLoader />
      </div>
    );
  }
    return (
      <div>
        <nav className="navbar navbar-expand-lg navbar-light bg-light">
          <div className="container-fluid">
            <Link className="navbar-brand fw-bold text-dark" to="/">YEMII</Link>
            <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse d-flex" id="navbarNav">
              <ul className="navbar-nav ms-auto">
                <ProfileButton/>         
              </ul>
            </div>
          </div>
        </nav>
        
        <div className="">
          <Routes>
            <Route exact path="/" element={<GlobalSearch />} />
            <Route exact path="/resultDoctor/:id" element={<ResultsFromDoctor />} />
            <Route exact path="/resultStructure/:param" element={<ResultsFromStructure />} />
            <Route exact path="/resultSpecialiteLieu/:param" element={<ResultsFromSpecialiteLieu />} />
            
            <Route
              path="/verify-email"
              element={<AuthenticationGuard component={EmailVerificationPage} />}
            />
            
            <Route
              path="/mon-profil"
              element={<EmailVerifiedGuard component={MonProfil} />}
            />
            <Route
              path="/user-profil"
              element={<EmailVerifiedGuard component={UserProfil} />}
            />
            <Route
              path="/confirmRdv/:param"
              element={<EmailVerifiedGuard component={ConfirmRdv} />}
            />
            <Route
              path="/homeAdmin"
              element={<EmailVerifiedGuard component={HomeAdmin} />}
            />
            <Route
              path="/homeMng"
              element={<EmailVerifiedGuard component={HomeMng} />}
            />
            <Route
              path="/homeSec"
              element={<EmailVerifiedGuard component={HomeSec} />}
            />
            <Route
              path="/homeMed"
              element={<EmailVerifiedGuard component={HomeMed} />}
            />
            <Route
              path="/homePat"
              element={<EmailVerifiedGuard component={HomePat} />}
            />
            <Route
              path="/show-structure"
              element={<EmailVerifiedGuard component={ShowStructure} />}
            />
            <Route
              path="/recette-globale"
              element={<EmailVerifiedGuard component={RecetteGlobale} />}
            />
            <Route
              path="/recette-structure"
              element={<EmailVerifiedGuard component={RecetteStructure} />}
            />
          </Routes>
        </div>
        {/* Définition – full width */}
        <section className="container-fluid px-0">
          <div
            className="py-4"
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, rgba(111,66,193,0.08) 100%)'
            }}
          >
            <div className="container">
              <div className="row text-center g-4">
                <div className="col-12 mb-4">
                  <h2 className="display-6 fw-light" style={{ color: '#6f6e70ff', letterSpacing: '-0.2px', marginTop: 48 }}>
                    Qu'est-ce que "YEMII" ?
                  </h2>
                  <div
                    className="mx-auto"
                    style={{ width: 120, height: 3, background: '#e9e6f5', marginTop: 30, marginBottom: 16, borderRadius: 2 }}
                  />
                  <div className="mx-auto ob-reveal" style={{ maxWidth: 860, marginTop: 12 }}>
                    <p className="text-muted lead mb-3" style={{ lineHeight: 1.9, fontSize: '1.7rem' }}>
                      YEMII est une application qui centralise les offres de prestation des structures de santé pour permettre aux patients d'accéder facilement à l'information, choisir et réserver en ligne.
                    </p>
                    <p className="mb-4 fw-semibold ob-reveal delay-2" style={{ color: '#4b2c91' }}>
                      Trouvez, comparez et réservez au bon endroit, au bon moment, au meilleur coût.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="container marketing mt-4 mt-lg-4">
          
          {/* Valeur pour le système de santé */}
          <div className="row text-center g-4 mb-4">
            <div className="col-12 mb-3">
              <h2 className="display-6 fw-light" style={{ color: '#6f6e70ff', letterSpacing: '-0.2px', marginTop: 48 }}>Valeur pour le système de santé</h2>
              <div className="mx-auto" style={{ width: 120, height: 3, background: '#e9e6f5', marginTop: 30, marginBottom: 16, borderRadius: 2 }} />
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img width="56" height="56" alt="Visibilité et accessibilité" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/globe2.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Visibilité et accessibilité</h4>
              <p className="small text-muted text-center">en publiant les offres de prestation pour la prise de rendez-vous en ligne</p>
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img width="56" height="56" alt="Redistribution de la demande" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/arrow-left-right.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Redistribution de la demande</h4>
              <p className="small text-muted text-center">vers les structures de petite taille et les médecins moins visibles.</p>
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img width="56" height="56" alt="Amélioration de la qualité" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/award.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Amélioration de la qualité</h4>
              <p className="small text-muted text-center">par la compétitivité et l’analyse du ressenti patient (collecte/compilation anonyme).</p>
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img width="56" height="56" alt="Pilotage" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/bar-chart-line.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Pilotage</h4>
              <p className="small text-muted text-center">via des tableaux de bord pour l’aide à la décision.</p>
            </div>
          </div>
          {/* Axes de valeur pour le patient */}
          <div className="row text-center g-4">
            <div className="col-12 mb-3">
              <h2 className="display-6 fw-light" style={{ color: '#6f6e70ff', letterSpacing: '-0.2px', marginTop: 48 }}>Valeur pour le patient</h2>
              <div className="mx-auto" style={{ width: 120, height: 3, background: '#e9e6f5', marginTop: 30, marginBottom: 16, borderRadius: 2 }} />
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover ob-reveal ob-delay-0">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img className="ob-icon" width="56" height="56" alt="Recommandation" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/hand-thumbs-up.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Recommandation</h4>
              <p className="small text-muted">Soyez orienté vers des spécialistes reconnus grâce aux retours patients.</p>
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover ob-reveal ob-delay-1">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img className="ob-icon" width="56" height="56" alt="Proximité" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/geo-alt.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Proximité</h4>
              <p className="small text-muted">Choisissez la structure sanitaire la plus proche de vos zones d’intérêt.</p>
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover ob-reveal ob-delay-2">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img className="ob-icon" width="56" height="56" alt="Timing" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/calendar2-check.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Timing</h4>
              <p className="small text-muted">Réservez selon vos disponibilités et évitez les files d’attente.</p>
            </div>
            <div className="col-6 col-lg-3 d-flex flex-column align-items-center ob-card-hover ob-reveal ob-delay-3">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 120, height: 120, backgroundColor: 'rgba(111,66,193,0.12)' }}>
                <img className="ob-icon" width="56" height="56" alt="Tarif" src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/cash-coin.svg" style={{ filter: 'invert(16%) sepia(36%) saturate(1314%) hue-rotate(242deg) brightness(90%) contrast(93%)' }} />
              </div>
              <h4 className="mt-3">Tarif</h4>
              <p className="small text-muted">Comparez les coûts, tenez compte de votre assurance, trouvez le meilleur prix.</p>
            </div>
          </div>

          {/* Profils utilisateurs */}
          <div className="row text-center g-4 mt-4 mb-4">
            <div className="col-12 mb-3">
              <h2 className="display-6 fw-light" style={{ color: '#6f6e70ff', letterSpacing: '-0.2px', marginTop: 48 }}>Comment ça marche?</h2>
              <div className="mx-auto" style={{ width: 120, height: 3, background: '#e9e6f5', marginTop: 30, marginBottom: 16, borderRadius: 2 }} />
            </div>
          </div>
          <div className="row featurette align-items-center ob-featurette">
            <div className="col-md-7">
              <h3 className="featurette-heading">Managers de structures</h3>
              <p className="lead">Publiez vos offres (spécialistes, examens, horaires, tarifs) et signalez les éventuelles indisponibilités.</p>
              <ul className="text-muted ob-checklist">
                <li>Gestion des plannings</li>
                <li>Signalement des indisponibilités (absence, appareil hors-service, manque de réactif)</li>
                <li>Tableaux de bord d’aide à la décision</li>
              </ul>
            </div>
            <div className="col-md-5 text-center">
              <div className="w-100" style={{ height: 320 }}>
                <img className="img-fluid rounded h-100 w-100 ob-ft-img" style={{ objectFit: 'cover' }} alt="Manager de structure sanitaire"
                     src="/manager4.jpg" />
              </div>
            </div>
          </div>

          <hr className="featurette-divider" />

          <div className="row featurette align-items-center ob-featurette">
            <div className="col-md-7 order-md-2">
              <h3 className="featurette-heading">Médecins</h3>
              <p className="lead">Visualisez votre agenda, suivez vos files d’attente, mesurez la satisfaction de vos patients.</p>
              <ul className="text-muted ob-checklist">
                <li>Agenda professionnel en temps réel</li>
                <li>Vue des files d’attente</li>
                <li>Indicateurs de performance et satisfaction</li>
              </ul>
            </div>
            <div className="col-md-5 order-md-1 text-center">
              <div className="w-100" style={{ height: 320 }}>
                <img className="img-fluid rounded h-100 w-100 ob-ft-img" style={{ objectFit: 'cover' }} alt="Médecin"
                     src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=900&auto=format&fit=crop" />
              </div>
            </div>
          </div>

          <hr className="featurette-divider" />

          <div className="row featurette align-items-center ob-featurette">
            <div className="col-md-7">
              <h3 className="featurette-heading">Secrétaires médicales</h3>
              <p className="lead">Gestion des patients en salle d'attente pour l'accès aux médecins.</p>
              <ul className="text-muted ob-checklist">
                <li>Visualisation des files d'attente par médecin</li>
                <li>Appel des patients</li>
                <li>Confirmation des prestations</li>
              </ul>
            </div>
            <div className="col-md-5 text-center">
              <div className="w-100" style={{ height: 320 }}>
                <img className="img-fluid rounded h-100 w-100 ob-ft-img" style={{ objectFit: 'cover' }} alt="Secrétaire médicale"
                     src="/secretaire.png" />
              </div>
            </div>
          </div>

          <hr className="featurette-divider" />

          <div className="row featurette align-items-center ob-featurette">
            <div className="col-md-7 order-md-2">
              <h3 className="featurette-heading">Patients</h3>
              <p className="lead">Recherchez, comparez et réservez selon vos critères. Évaluez votre expérience pour améliorer la qualité des services.</p>
              <ul className="text-muted ob-checklist">
                <li>Recherche multi-critères (médecin, structure, spécialité, lieu)</li>
                <li>Prise/annulation de rendez-vous</li>
                <li>Évaluation anonyme de la prestation</li>
              </ul>
            </div>
            <div className="col-md-5 order-md-1 text-center">
              <div className="w-100" style={{ height: 320 }}>
                <img className="img-fluid rounded h-100 w-100 ob-ft-img" style={{ objectFit: 'cover' }} alt="Patients"
                     src="/patient.png" />
              </div>
            </div>
          </div>

          <hr className="featurette-divider" />
        </div>
      
        <footer className="container py-5">
          <div className="row">
            <div className="col-12 col-md-3 mb-3">
              <a className="navbar-brand d-inline-flex align-items-center mb-2" href="/">
                <span className="fw-bold">YEMII</span>
              </a>
              <small className="d-block text-muted">Plateforme d’information médicale et de prise de rendez-vous</small>
              <small className="d-block text-muted">© {new Date().getFullYear()} Yemii</small>
              <div className="mt-3 d-flex align-items-center gap-3">
                <a href="#" aria-label="Facebook" target="_blank" rel="noreferrer">
                  <img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/facebook.svg" width="24" height="24" alt="Facebook" />
                </a>
                <a href="#" aria-label="LinkedIn" target="_blank" rel="noreferrer">
                  <img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/linkedin.svg" width="24" height="24" alt="LinkedIn" />
                </a>
                <a href="#" aria-label="X (Twitter)" target="_blank" rel="noreferrer">
                  <img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/twitter-x.svg" width="24" height="24" alt="X (Twitter)" />
                </a>
                <a href="#" aria-label="YouTube" target="_blank" rel="noreferrer">
                  <img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/youtube.svg" width="24" height="24" alt="YouTube" />
                </a>
                <a href="#" aria-label="Instagram" target="_blank" rel="noreferrer">
                  <img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/icons/instagram.svg" width="24" height="24" alt="Instagram" />
                </a>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <h5>À propos</h5>
              <ul className="list-unstyled text-small">
                <li><a className="text-muted" href="/">Notre mission</a></li>
                <li><a className="text-muted" href="/">Équipe</a></li>
                <li><a className="text-muted" href="/">Contact</a></li>
                <li><a className="text-muted" href="/">Presse</a></li>
              </ul>
            </div>
            <div className="col-6 col-md-3">
              <h5>Informations légales</h5>
              <ul className="list-unstyled text-small">
                <li><a className="text-muted" href="/">Mentions Légales</a></li>
                <li><a className="text-muted" href="/">Politique de Confidentialité</a></li>
                <li><a className="text-muted" href="/">Conditions d’Utilisation</a></li>
              </ul>
            </div>
            <div className="col-6 col-md-3">
              <h5>Support</h5>
              <ul className="list-unstyled text-small">
                <li><a className="text-muted" href="/">Centre d’aide</a></li>
                <li><a className="text-muted" href="/">FAQ</a></li>
              </ul>
            </div>
          </div>
        </footer>

      </div>  
       
    );
  }

export default App;
